import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import useSWR from 'swr';
import api from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { exportToCSV } from '../../../services/exportService';
import { exportToExcel } from '../../../components/exports';
import { Column } from '../../../components/DataTable/DataTable';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { cn } from '../../../lib/utils';
import { copyTextToClipboard } from '../../../utils/copyToClipboard';
import { FlyoutRailBackdrop, FlyoutRailPanel } from '../../../components/ui/FlyoutFromRail';

const BASE = '/database-browser';

const fetcher = (url: string) => api.get(url).then((r) => r.data);

type TableMeta = { tableName: string; tableSchema: string };
type ColumnMeta = {
  columnName: string;
  dataType: string;
  isNullable: string;
  columnDefault: string | null;
  isPrimaryKey: boolean;
};
type TableDataRes = { data: Record<string, unknown>[]; total: number; page: number; pageSize: number };

const BULK_SELECT_DELETE_MAX = 1000;

const EMPTY_TABLE_BLOCKED = new Set(['_prisma_migrations']);

// Icons as inline SVG components for consistency
const TableIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
  </svg>
);
const DatabaseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
  </svg>
);
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);
const ColumnsIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);
const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);
const PlayIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);
const PencilIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);
const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);
const XIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);
const CopyIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);
const DuplicateIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);
const SortUpIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);
const SortDownIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);
const MenuIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const RECENT_QUERIES_KEY = 'db-browser-recent-queries';
const RECENT_QUERIES_MAX = 10;
const DATE_TYPES = ['date', 'timestamp', 'timestamp with time zone', 'timestamp without time zone', 'timestamptz', 'timestamp without time zone'];

/**
 * Email rows store From/To/Cc/Bcc as JSON arrays of { name?, email }.
 * Format as a readable list; return null if the shape is not a participant list.
 */
function formatEmailParticipantList(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return '';
  const parts: string[] = [];
  for (const item of value) {
    if (item == null) continue;
    if (typeof item === 'string') {
      parts.push(item);
      continue;
    }
    if (typeof item === 'object' && item !== null && 'email' in item) {
      const email = (item as { email?: unknown }).email;
      if (typeof email !== 'string') return null;
      const rawName = (item as { name?: unknown }).name;
      const nameStr = typeof rawName === 'string' ? rawName.trim() : '';
      parts.push(nameStr ? `${nameStr} <${email}>` : email);
      continue;
    }
    return null;
  }
  return parts.join(', ');
}

function formatCellValue(value: unknown, dataType?: string): string {
  if (value === null || value === undefined) return '(null)';
  const type = (dataType || '').toLowerCase();
  if (DATE_TYPES.some((t) => type.includes(t))) {
    try {
      const d = new Date(value as string | number);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    } catch {}
  }
  const emailList = formatEmailParticipantList(value);
  if (emailList !== null) return emailList;
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export default function DatabaseBrowserPage() {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [password, setPassword] = useState('');
  const [passwordUnlocked, setPasswordUnlocked] = useState<boolean | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'data' | 'sql'>('data');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(null);
  const [editingDraft, setEditingDraft] = useState<Record<string, unknown>>({});
  const [addingRow, setAddingRow] = useState(false);
  const [newRow, setNewRow] = useState<Record<string, unknown>>({});
  const [rowToDelete, setRowToDelete] = useState<Record<string, unknown> | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [emptyTableModalOpen, setEmptyTableModalOpen] = useState(false);
  const [emptyConfirmName, setEmptyConfirmName] = useState('');
  const [emptyCascade, setEmptyCascade] = useState(false);
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [selectAllMatchingLoading, setSelectAllMatchingLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tablesFlyoutOpen, setTablesFlyoutOpen] = useState(false);
  const dbAsideRef = useRef<HTMLElement | null>(null);
  const [tablesFlyoutLeft, setTablesFlyoutLeft] = useState(48);
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM ');
  const [sqlResult, setSqlResult] = useState<Record<string, unknown>[] | null>(null);
  const [sqlError, setSqlError] = useState<string | null>(null);
  const [recentQueries, setRecentQueries] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(RECENT_QUERIES_KEY);
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });
  const sqlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (passwordUnlocked !== null) return;
    setUnlockError(null);
    api
      .post(`${BASE}/validate-password`, { password: '' })
      .then((r) => r.data)
      .then((d: { valid: boolean }) => setPasswordUnlocked(d.valid))
      .catch((e: any) => {
        setPasswordUnlocked(false);
        setUnlockError(e.response?.data?.message || e.message || 'Connection failed');
      });
  }, [passwordUnlocked]);

  const submitPassword = useCallback(() => {
    setUnlockError(null);
    api
      .post(`${BASE}/validate-password`, { password })
      .then((r) => r.data)
      .then((d: { valid: boolean }) => {
        if (d.valid) {
          setPasswordUnlocked(true);
          setUnlockError(null);
        } else setUnlockError('Invalid password');
      })
      .catch((e: any) => setUnlockError(e.response?.data?.message || e.message || 'Unlock failed'));
  }, [password]);

  const retryUnlock = useCallback(() => {
    setPasswordUnlocked(null);
    setUnlockError(null);
  }, []);

  const { data: tables = [], mutate: mutateTables, isLoading: tablesLoading } = useSWR<TableMeta[]>(
    passwordUnlocked ? `${BASE}/tables` : null,
    fetcher
  );

  const filteredTables = useMemo(() => {
    if (!tableFilter.trim()) return tables;
    const q = tableFilter.toLowerCase();
    return tables.filter((t) => t.tableName.toLowerCase().includes(q));
  }, [tables, tableFilter]);

  useLayoutEffect(() => {
    if (!tablesFlyoutOpen) return;
    const update = () => {
      const el = dbAsideRef.current;
      setTablesFlyoutLeft(el ? el.getBoundingClientRect().right : 48);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [tablesFlyoutOpen]);

  useEffect(() => {
    if (!sidebarCollapsed) setTablesFlyoutOpen(false);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!tablesFlyoutOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTablesFlyoutOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tablesFlyoutOpen]);

  const { data: columns = [], isLoading: columnsLoading } = useSWR<ColumnMeta[]>(
    passwordUnlocked && selectedTable ? `${BASE}/tables/${encodeURIComponent(selectedTable)}/columns` : null,
    fetcher
  );

  const dataUrl = passwordUnlocked && selectedTable
    ? `${BASE}/tables/${encodeURIComponent(selectedTable)}/data?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(searchDebounced)}${sortColumn ? `&orderBy=${encodeURIComponent(sortColumn)}&orderDir=${sortDir}` : ''}`
    : null;
  const { data: tableData, mutate: mutateTableData, isLoading: tableDataLoading } = useSWR<TableDataRes>(dataUrl, fetcher);

  const { data: statistics, mutate: mutateStatistics } = useSWR<{ rowCount?: number }>(
    passwordUnlocked && selectedTable
      ? `${BASE}/tables/${encodeURIComponent(selectedTable)}/statistics`
      : null,
    fetcher
  );

  const { data: foreignKeys = [] } = useSWR<Record<string, unknown>[]>(
    passwordUnlocked && selectedTable
      ? `${BASE}/tables/${encodeURIComponent(selectedTable)}/foreign-keys`
      : null,
    fetcher
  );

  const { data: referencingTables = [] } = useSWR<Record<string, unknown>[]>(
    passwordUnlocked && selectedTable
      ? `${BASE}/tables/${encodeURIComponent(selectedTable)}/referencing-tables`
      : null,
    fetcher
  );

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (selectedTable) setPage(1);
  }, [selectedTable, searchDebounced]);

  useEffect(() => {
    if (columns.length > 0) {
      setVisibleColumns(new Set(columns.map((c) => c.columnName)));
    }
  }, [selectedTable, columns]);

  const tableColumns: Column<Record<string, unknown>>[] = useMemo(
    () =>
      columns.map((c) => ({
        key: c.columnName,
        header: c.columnName + (c.isPrimaryKey ? ' (PK)' : ''),
      })),
    [columns]
  );

  const visibleCols = useMemo(
    () => columns.filter((c) => visibleColumns.has(c.columnName)),
    [columns, visibleColumns]
  );

  const pkColumns = useMemo(() => columns.filter((c) => c.isPrimaryKey), [columns]);
  const canSelectRows = pkColumns.length > 0;
  const canEmptyTable = selectedTable != null && !EMPTY_TABLE_BLOCKED.has(selectedTable);

  const getRowId = useCallback(
    (row: Record<string, unknown>) => {
      return pkColumns.map((c) => String(row[c.columnName] ?? '')).join(',');
    },
    [pkColumns]
  );

  useEffect(() => {
    setSelectedRowIds(new Set());
  }, [selectedTable, searchDebounced, sortColumn, sortDir]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete || !selectedTable) return;
    const id = getRowId(rowToDelete);
    setDeleteLoading(true);
    try {
      await api.delete(`${BASE}/tables/${encodeURIComponent(selectedTable)}/data/${encodeURIComponent(id)}`);
      setRowToDelete(null);
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      showSuccess('Row deleted.');
      mutateTableData();
      mutateTables();
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  }, [rowToDelete, selectedTable, getRowId, mutateTableData, mutateTables, showSuccess, showError]);

  const toggleSelectRow = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const pageRowIds = useMemo(
    () => (tableData?.data ?? []).map((row) => getRowId(row)),
    [tableData?.data, getRowId]
  );

  const allPageSelected =
    pageRowIds.length > 0 && pageRowIds.every((id) => selectedRowIds.has(id));
  const somePageSelected = pageRowIds.some((id) => selectedRowIds.has(id));

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageRowIds.forEach((id) => next.delete(id));
      } else {
        pageRowIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageRowIds]);

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) {
      el.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const selectAllMatchingResults = useCallback(async () => {
    if (!selectedTable || !passwordUnlocked) return;
    const total = tableData?.total ?? 0;
    if (total === 0) return;
    if (total > BULK_SELECT_DELETE_MAX) {
      showError(
        `You can select at most ${BULK_SELECT_DELETE_MAX} rows. Refine search or filter. (${total} rows match.)`
      );
      return;
    }
    setSelectAllMatchingLoading(true);
    try {
      const ids = new Set<string>();
      const pages = Math.ceil(total / pageSize);
      const sortQs =
        sortColumn != null && sortColumn !== ''
          ? `&orderBy=${encodeURIComponent(sortColumn)}&orderDir=${sortDir}`
          : '';
      for (let p = 1; p <= pages; p++) {
        const url = `${BASE}/tables/${encodeURIComponent(selectedTable)}/data?page=${p}&pageSize=${pageSize}&search=${encodeURIComponent(searchDebounced)}${sortQs}`;
        const res = await api.get<TableDataRes>(url);
        res.data.data.forEach((row) => ids.add(getRowId(row)));
      }
      setSelectedRowIds(ids);
      showSuccess(`Selected ${ids.size} row(s).`);
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Failed to load rows');
    } finally {
      setSelectAllMatchingLoading(false);
    }
  }, [
    selectedTable,
    passwordUnlocked,
    tableData?.total,
    pageSize,
    searchDebounced,
    sortColumn,
    sortDir,
    getRowId,
    showSuccess,
    showError,
  ]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (!selectedTable || selectedRowIds.size === 0) return;
    const ids = Array.from(selectedRowIds);
    const n = ids.length;
    setBulkDeleteLoading(true);
    try {
      await api.post(`${BASE}/tables/${encodeURIComponent(selectedTable)}/data/bulk-delete`, { ids });
      setBulkDeleteConfirmOpen(false);
      setSelectedRowIds(new Set());
      showSuccess(`Deleted ${n} row(s).`);
      mutateTableData();
      mutateTables();
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Bulk delete failed');
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [selectedTable, selectedRowIds, mutateTableData, mutateTables, showSuccess, showError]);

  const handleEmptyTable = useCallback(async () => {
    if (!selectedTable || emptyConfirmName !== selectedTable) return;
    setEmptyLoading(true);
    try {
      await api.post(
        `${BASE}/tables/${encodeURIComponent(selectedTable)}/empty`,
        { confirmTableName: emptyConfirmName, cascade: emptyCascade },
        { skipGlobalErrorToast: true }
      );
      setEmptyTableModalOpen(false);
      setEmptyConfirmName('');
      setEmptyCascade(false);
      setSelectedRowIds(new Set());
      setEditingRow(null);
      setEditingDraft({});
      setAddingRow(false);
      showSuccess('Table emptied.');
      mutateTableData();
      mutateTables();
      mutateStatistics();
    } catch (e: any) {
      const m = e.response?.data?.message;
      const errText =
        Array.isArray(m) ? m.join(' ') : typeof m === 'string' ? m : e.message || 'Empty table failed';
      showError(errText);
    } finally {
      setEmptyLoading(false);
    }
  }, [
    selectedTable,
    emptyConfirmName,
    emptyCascade,
    mutateTableData,
    mutateTables,
    mutateStatistics,
    showSuccess,
    showError,
  ]);

  const handleSaveEdit = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!selectedTable || !editingRow) return;
      const id = getRowId(editingRow);
      try {
        await api.patch(
          `${BASE}/tables/${encodeURIComponent(selectedTable)}/data/${encodeURIComponent(id)}`,
          payload
        );
        setEditingRow(null);
        setEditingDraft({});
        showSuccess('Row updated.');
        mutateTableData();
        mutateTables();
      } catch (e: any) {
        showError(e.response?.data?.message || e.message || 'Update failed');
      }
    },
    [selectedTable, editingRow, getRowId, mutateTableData, mutateTables, showSuccess, showError]
  );

  const handleCreate = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!selectedTable) return;
      try {
        await api.post(`${BASE}/tables/${encodeURIComponent(selectedTable)}/data`, payload);
        setAddingRow(false);
        setNewRow({});
        showSuccess('Row created.');
        mutateTableData();
        mutateTables();
      } catch (e: any) {
        showError(e.response?.data?.message || e.message || 'Insert failed');
      }
    },
    [selectedTable, mutateTableData, mutateTables, showSuccess, showError]
  );

  const saveRecentQuery = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed.toUpperCase().startsWith('SELECT')) return;
    setRecentQueries((prev) => {
      const next = [trimmed, ...prev.filter((x) => x !== trimmed)].slice(0, RECENT_QUERIES_MAX);
      try { localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const runSql = useCallback(async () => {
    setSqlError(null);
    setSqlResult(null);
    try {
      const res = await api.post(`${BASE}/query`, { query: sqlQuery, maxRows: 1000 });
      setSqlResult(res.data);
      saveRecentQuery(sqlQuery);
      showSuccess(`Query returned ${res.data?.length ?? 0} row(s).`);
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Query failed';
      setSqlError(msg);
      showError(msg);
    }
  }, [sqlQuery, showSuccess, showError, saveRecentQuery]);

  const handleRefresh = useCallback(() => {
    mutateTableData();
    mutateTables();
    mutateStatistics();
    showSuccess('Data refreshed.');
  }, [mutateTableData, mutateTables, mutateStatistics, showSuccess]);

  const handleSortColumn = useCallback((col: string) => {
    setSortColumn((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
    setPage(1);
  }, []);

  const copyCell = useCallback((value: unknown, dataType?: string) => {
    const text =
      value === null || value === undefined ? '' : formatCellValue(value, dataType);
    const clip = text === '(null)' ? '' : text;
    void copyTextToClipboard(clip).then((ok) => {
      if (ok) showSuccess('Copied to clipboard.');
      else showError('Copy failed (clipboard unavailable in this browser or context).');
    });
  }, [showSuccess, showError]);

  const duplicateRow = useCallback((row: Record<string, unknown>) => {
    const pkSet = new Set(pkColumns.map((c) => c.columnName));
    const copy = { ...row };
    pkSet.forEach((k) => delete copy[k]);
    setNewRow(copy);
    setAddingRow(true);
  }, [pkColumns]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingRow) { setEditingRow(null); setEditingDraft({}); }
        else if (addingRow) { setAddingRow(false); setNewRow({}); }
        else if (showColumnPicker) setShowColumnPicker(false);
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && activeTab === 'sql') {
        e.preventDefault();
        runSql();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingRow, addingRow, showColumnPicker, activeTab, runSql]);

  const handleExportCsv = useCallback(() => {
    if (!tableData?.data.length || !selectedTable) return;
    const exportData = {
      sortedData: tableData.data,
      tableColumns: tableColumns.filter((c) => visibleColumns.has(String(c.key))),
      entityName: selectedTable,
      exportFileName: `db-${selectedTable}-${new Date().toISOString().slice(0, 10)}`,
    };
    exportToCSV(exportData);
  }, [tableData, selectedTable, tableColumns, visibleColumns]);

  const handleExportExcel = useCallback(() => {
    if (!tableData?.data.length || !selectedTable) return;
    const exportData = {
      sortedData: tableData.data,
      tableColumns: tableColumns.filter((c) => visibleColumns.has(String(c.key))),
      entityName: selectedTable,
      exportFileName: `db-${selectedTable}-${new Date().toISOString().slice(0, 10)}`,
    };
    exportToExcel(exportData);
  }, [tableData, selectedTable, tableColumns, visibleColumns]);

  const toggleColumn = (col: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  const totalPages = tableData ? Math.ceil(tableData.total / pageSize) : 0;

  if (passwordUnlocked === null) {
    return (
      <div className={cn('flex flex-col items-center justify-center min-h-[320px] gap-4', colors.background, colors.text)}>
        <div className="animate-pulse">
          <DatabaseIcon />
        </div>
        <p className={cn('text-sm', colors.textMuted)}>Connecting to database...</p>
      </div>
    );
  }

  if (passwordUnlocked === false) {
    return (
      <div className={cn('min-h-[400px] flex items-center justify-center p-6', colors.background)}>
        <div className={cn('w-full max-w-sm rounded-xl border shadow-lg p-6', colors.surface, colors.border)}>
          <div className={cn('flex justify-center mb-4', colors.textMuted)}>
            <DatabaseIcon />
          </div>
          <h2 className={cn('text-lg font-semibold text-center mb-1', colors.text)}>Database Browser</h2>
          <p className={cn('text-sm text-center mb-5', colors.textMuted)}>Enter the password to continue.</p>
          {unlockError && (
            <p className={cn('text-sm text-center mb-3', colors.error || 'text-red-600')}>{unlockError}</p>
          )}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitPassword()}
            className={cn('w-full border rounded-lg px-3 py-2.5 mb-4 text-sm', colors.border, colors.surface, colors.text)}
            placeholder="Password"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitPassword}
              className={cn('flex-1 py-2.5 rounded-lg text-sm font-medium', colors.primary, colors.primaryText)}
            >
              Unlock
            </button>
            <button
              type="button"
              onClick={retryUnlock}
              className={cn('py-2.5 px-4 rounded-lg text-sm border', colors.border, colors.text)}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full overflow-hidden', colors.background, colors.text)}>
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          ref={dbAsideRef}
          className={cn(
            'border-r flex flex-col shrink-0 transition-[width] overflow-hidden',
            sidebarCollapsed ? 'w-12' : 'w-64',
            colors.border,
            colors.surface
          )}
        >
          <div
            className={cn(
              'border-b shrink-0 flex items-center gap-2',
              colors.border,
              sidebarCollapsed ? 'p-2 flex-col' : 'p-3'
            )}
          >
            {!sidebarCollapsed && (
              <div className="relative flex-1 min-w-0">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                  placeholder="Filter tables..."
                  className={cn('w-full pl-8 pr-3 py-2 text-sm rounded-lg border', colors.border, colors.background, colors.text)}
                />
              </div>
            )}
            {sidebarCollapsed && (
              <button
                type="button"
                onClick={() => setTablesFlyoutOpen((o) => !o)}
                className={cn(
                  'p-2 rounded-lg shrink-0',
                  colors.textMuted,
                  colors.interactiveHover,
                  tablesFlyoutOpen && colors.primaryBorderAll
                )}
                title="Browse tables"
                aria-label="Browse tables"
                aria-expanded={tablesFlyoutOpen}
              >
                <TableIcon />
              </button>
            )}
            <button
              type="button"
              onClick={() => setSidebarCollapsed((v) => !v)}
              className={cn('p-2 rounded-lg shrink-0', colors.textMuted, colors.interactiveHover)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <MenuIcon />
            </button>
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              {tablesLoading ? (
                <p className={cn('text-sm py-4 text-center', colors.textMuted)}>Loading tables...</p>
              ) : filteredTables.length === 0 ? (
                <p className={cn('text-sm py-4 text-center', colors.textMuted)}>
                  {tableFilter ? 'No tables match' : 'No tables'}
                </p>
              ) : (
                <div className="space-y-0.5">
                  {filteredTables.map((t) => (
                    <button
                      key={t.tableName}
                      type="button"
                      onClick={() => {
                        setSelectedTable(t.tableName);
                        setEditingRow(null);
                        setAddingRow(false);
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg text-sm transition-colors',
                        selectedTable === t.tableName
                          ? cn(colors.primary, colors.primaryText)
                          : cn(colors.text, colors.interactiveHover)
                      )}
                    >
                      <TableIcon />
                      <span className="truncate">{t.tableName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {!selectedTable ? (
            <div className={cn('flex-1 flex flex-col items-center justify-center gap-4 p-8', colors.textMuted)}>
              <div className={cn('p-4 rounded-full', colors.surface, colors.border)}>
                <TableIcon />
              </div>
              <p className="text-sm font-medium">Select a table</p>
              <p className="text-sm">Choose a table from the sidebar to view and edit its data.</p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className={cn('flex flex-wrap items-center gap-3 px-4 py-3 border-b shrink-0', colors.border, colors.surface)}>
                <div className="flex items-center gap-2">
                  <span className={cn('font-medium text-sm', colors.text)}>{selectedTable}</span>
                  {statistics != null && (
                    <span className={cn('text-xs px-2 py-0.5 rounded-full', colors.border, colors.textMuted)}>
                      ~{statistics.rowCount ?? 0} rows
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-auto">
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
                    title="Refresh"
                  >
                    <RefreshIcon />
                    {activeTab === 'data' && <span>Refresh</span>}
                  </button>
                  <div className={cn('flex rounded-lg border overflow-hidden', colors.border)}>
                    <button
                      type="button"
                      onClick={() => setActiveTab('data')}
                      className={cn(
                        'px-3 py-1.5 text-sm font-medium',
                        activeTab === 'data' ? cn(colors.primary, colors.primaryText) : colors.text
                      )}
                    >
                      Data
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('sql')}
                      className={cn(
                        'px-3 py-1.5 text-sm font-medium border-l',
                        activeTab === 'sql' ? cn(colors.primary, colors.primaryText) : colors.text,
                        colors.border
                      )}
                    >
                      SQL
                    </button>
                  </div>
                  {activeTab === 'data' && (
                    <>
                      <div className={cn('w-px h-6', colors.border)} />
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowColumnPicker((v) => !v)}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
                          title="Column visibility"
                        >
                          <ColumnsIcon />
                          Columns
                        </button>
                        {showColumnPicker && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowColumnPicker(false)} />
                            <div
                              className={cn(
                                'absolute right-0 top-full mt-1 z-20 rounded-lg border shadow-lg p-3 max-h-64 overflow-y-auto min-w-[180px]',
                                colors.surface,
                                colors.border
                              )}
                            >
                              <div className={cn('text-xs font-medium mb-2', colors.textMuted)}>Visible columns</div>
                              <div className="space-y-1.5">
                                {columns.map((c) => (
                                  <label key={c.columnName} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={visibleColumns.has(c.columnName)}
                                      onChange={() => toggleColumn(c.columnName)}
                                      className="rounded"
                                    />
                                    <span className="truncate">{c.columnName}</span>
                                    {c.isPrimaryKey && <span className={cn('text-xs', colors.textMuted)}>PK</span>}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={handleExportCsv}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
                          title="Export CSV"
                        >
                          <DownloadIcon />
                          CSV
                        </button>
                        <button
                          type="button"
                          onClick={handleExportExcel}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
                          title="Export Excel"
                        >
                          <DownloadIcon />
                          Excel
                        </button>
                      </div>
                      {!addingRow && (
                        <button
                          type="button"
                          onClick={() => setAddingRow(true)}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium', colors.primary, colors.primaryText)}
                        >
                          <PlusIcon />
                          Add row
                        </button>
                      )}
                      {!addingRow && canEmptyTable && (
                        <button
                          type="button"
                          onClick={() => {
                            setEmptyConfirmName('');
                            setEmptyCascade(false);
                            setEmptyTableModalOpen(true);
                          }}
                          className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium border-red-700/50 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40'
                          )}
                          title="Remove all rows from this table (with confirmation)"
                        >
                          <TrashIcon />
                          Empty table
                        </button>
                      )}
                      {canSelectRows && selectedRowIds.size > 0 && (
                        <button
                          type="button"
                          onClick={() => setBulkDeleteConfirmOpen(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-700 bg-red-600 text-white hover:bg-red-700"
                        >
                          <TrashIcon />
                          Delete selected ({selectedRowIds.size})
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {activeTab === 'data' && (
                <>
                  {/* Search + metadata toggle */}
                  <div className={cn('flex items-center gap-3 px-4 py-2 border-b', colors.border)}>
                    <div className="relative flex-1 max-w-sm">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2">
                        <SearchIcon />
                      </span>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search in table..."
                        className={cn('w-full pl-8 pr-3 py-2 text-sm rounded-lg border', colors.border, colors.surface, colors.text)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMetadata((v) => !v)}
                      className={cn('text-sm', showMetadata ? colors.primaryFg : colors.textMuted)}
                    >
                      {showMetadata ? 'Hide' : 'Show'} metadata
                    </button>
                  </div>

                  {/* Add row form */}
                  {addingRow && (
                    <div className={cn('px-4 py-4 border-b', colors.border, colors.surface)}>
                      <div className={cn('text-sm font-medium mb-3', colors.text)}>New row</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {columns.map((col) => (
                          <div key={col.columnName}>
                            <label className={cn('block text-xs font-medium mb-1', colors.textMuted)}>{col.columnName}</label>
                            <input
                              type="text"
                              value={String(newRow[col.columnName] ?? '')}
                              onChange={(e) =>
                                setNewRow((prev) => ({ ...prev, [col.columnName]: e.target.value || null }))
                              }
                              className={cn('w-full border rounded-lg px-2.5 py-1.5 text-sm', colors.border, colors.surface, colors.text)}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => handleCreate(newRow)}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium', colors.primary, colors.primaryText)}
                        >
                          <CheckIcon />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => { setAddingRow(false); setNewRow({}); }}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
                        >
                          <XIcon />
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Data table */}
                  <div className="flex-1 flex flex-col min-h-0">
                    {columnsLoading ? (
                      <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>Loading columns...</div>
                    ) : tableDataLoading ? (
                      <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>Loading data...</div>
                    ) : tableData && tableData.data.length === 0 ? (
                      <div className={cn('flex flex-col items-center justify-center py-12 gap-4', colors.textMuted)}>
                        <p className="text-sm font-medium">No rows</p>
                        <p className="text-sm">This table is empty or no rows match your search.</p>
                        {!addingRow && (
                          <button
                            type="button"
                            onClick={() => setAddingRow(true)}
                            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium', colors.primary, colors.primaryText)}
                          >
                            <PlusIcon />
                            Add first row
                          </button>
                        )}
                      </div>
                    ) : (
                    <div className="flex-1 min-h-0 overflow-auto">
                    <table className="w-full border-collapse text-sm min-w-max">
                      <thead className={cn('sticky top-0 z-10', colors.surface)}>
                        <tr className={cn('border-b', colors.border)}>
                          {canSelectRows && (
                            <th className={cn('text-left py-3 px-2 font-medium w-10', colors.text)}>
                              <input
                                ref={selectAllCheckboxRef}
                                type="checkbox"
                                checked={allPageSelected && pageRowIds.length > 0}
                                onChange={toggleSelectAllOnPage}
                                className="rounded"
                                title="Select all rows on this page"
                                aria-label="Select all rows on this page"
                              />
                            </th>
                          )}
                          <th className={cn('text-left py-3 px-4 font-medium w-12', colors.text)}>#</th>
                          <th className={cn('text-left py-3 px-4 font-medium w-36 min-w-[9rem]', colors.text)}>Actions</th>
                          {visibleCols.map((c) => (
                            <th
                              key={c.columnName}
                              className={cn('text-left py-3 px-4 font-medium cursor-pointer select-none whitespace-nowrap', colors.text)}
                              onClick={() => handleSortColumn(c.columnName)}
                            >
                              <span className="inline-flex items-center gap-1">
                                {c.columnName}
                                {c.isPrimaryKey && <span className={cn('text-xs', colors.textMuted)}>(PK)</span>}
                                {sortColumn === c.columnName && (sortDir === 'asc' ? <SortUpIcon /> : <SortDownIcon />)}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(tableData?.data ?? []).map((row, idx) => {
                          const rowId = getRowId(row);
                          const isEditing = editingRow && getRowId(editingRow) === rowId;
                          const draft = isEditing ? editingDraft : row;
                          return (
                            <tr
                              key={rowId}
                              className={cn(
                                'border-b transition-colors',
                                colors.border,
                                isEditing && colors.surfaceHover
                              )}
                            >
                              {canSelectRows && (
                                <td className={cn('py-2 px-2 align-top')}>
                                  <input
                                    type="checkbox"
                                    checked={selectedRowIds.has(rowId)}
                                    onChange={() => toggleSelectRow(rowId)}
                                    className="rounded mt-1"
                                    aria-label={`Select row ${(page - 1) * pageSize + idx + 1}`}
                                  />
                                </td>
                              )}
                              <td className={cn('py-2 px-4', colors.textMuted)}>{(page - 1) * pageSize + idx + 1}</td>
                              <td className={cn('py-2 px-4 align-top min-w-[9rem]', colors.text)}>
                                {isEditing ? (
                                  <span className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleSaveEdit(editingDraft)}
                                      className={cn('p-1.5 rounded hover:opacity-80', colors.primary, colors.primaryText)}
                                      title="Save"
                                    >
                                      <CheckIcon />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingRow(null); setEditingDraft({}); }}
                                      className={cn('p-1.5 rounded', colors.textMuted, `hover:${colors.surfaceHover}`)}
                                      title="Cancel"
                                    >
                                      <XIcon />
                                    </button>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => { setEditingRow(row); setEditingDraft({ ...row }); }}
                                      className={cn('p-1.5 rounded', colors.textMuted, `hover:${colors.primaryFg}`)}
                                      title="Edit"
                                    >
                                      <PencilIcon />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => duplicateRow(row)}
                                      className={cn('p-1.5 rounded', colors.textMuted, `hover:${colors.primaryFg}`)}
                                      title="Duplicate row"
                                    >
                                      <DuplicateIcon />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setRowToDelete(row)}
                                      className={cn('p-1.5 rounded', colors.textMuted, `hover:${colors.error}`)}
                                      title="Delete"
                                    >
                                      <TrashIcon />
                                    </button>
                                  </span>
                                )}
                              </td>
                              {visibleCols.map((col) => (
                                <td key={col.columnName} className="py-2 px-4 align-top">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={String(draft[col.columnName] ?? '')}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setEditingDraft((prev) => ({
                                          ...prev,
                                          [col.columnName]: v === '' ? null : v,
                                        }));
                                      }}
                                      className={cn('w-full border rounded px-2 py-1 text-sm', colors.border, colors.surface, colors.text)}
                                    />
                                  ) : (
                                    <span className="inline-flex items-center gap-1 max-w-[220px]">
                                      <span className="truncate block">{formatCellValue(row[col.columnName], col.dataType)}</span>
                                      <button
                                        type="button"
                                        onClick={() => copyCell(row[col.columnName], col.dataType)}
                                        className={cn('p-1 rounded shrink-0 opacity-60 hover:opacity-100', colors.textMuted)}
                                        title="Copy cell"
                                      >
                                        <CopyIcon />
                                      </button>
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {tableData && tableData.total > 0 && (
                    <div className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t', colors.border, colors.surface)}>
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <span className={cn('text-sm', colors.textMuted)}>
                          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, tableData.total)} of {tableData.total}
                        </span>
                        {canSelectRows && tableData.total <= BULK_SELECT_DELETE_MAX && (
                          <button
                            type="button"
                            disabled={selectAllMatchingLoading}
                            onClick={() => void selectAllMatchingResults()}
                            className={cn('text-sm text-left underline-offset-2 hover:underline disabled:opacity-50', colors.primaryFg || colors.text)}
                          >
                            {selectAllMatchingLoading
                              ? 'Selecting all…'
                              : `Select all ${tableData.total} matching row${tableData.total === 1 ? '' : 's'}`}
                          </button>
                        )}
                        {canSelectRows && tableData.total > BULK_SELECT_DELETE_MAX && (
                          <span className={cn('text-xs', colors.textMuted)}>
                            Select-all is limited to {BULK_SELECT_DELETE_MAX} rows; refine search to enable.
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={pageSize}
                          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                          className={cn('border rounded-lg px-2.5 py-1.5 text-sm', colors.border, colors.surface, colors.text)}
                        >
                          {[10, 25, 50, 100, 200].map((n) => (
                            <option key={n} value={n}>
                              {n} per page
                            </option>
                          ))}
                        </select>
                        <div className={cn('flex items-center rounded-lg border overflow-hidden', colors.border)}>
                          <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className={cn('p-2 disabled:opacity-40', colors.text)}
                          >
                            <ChevronLeftIcon />
                          </button>
                          <span className={cn('px-3 py-1.5 text-sm', colors.textMuted)}>
                            {page} / {totalPages || 1}
                          </span>
                          <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className={cn('p-2 disabled:opacity-40', colors.text)}
                          >
                            <ChevronRightIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Metadata (collapsible) */}
                  {showMetadata && (foreignKeys.length > 0 || referencingTables.length > 0) && (
                    <div className={cn('px-4 py-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4', colors.border, colors.surface)}>
                      <div>
                        <div className={cn('text-xs font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>Foreign keys</div>
                        <ul className="space-y-1 text-sm">
                          {foreignKeys.map((fk, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span className={colors.text}>{(fk.columnName as string)}</span>
                              <span className={colors.textMuted}>→</span>
                              <span className={colors.text}>{(fk.foreignTable as string)}</span>
                            </li>
                          ))}
                          {foreignKeys.length === 0 && <li className={colors.textMuted}>None</li>}
                        </ul>
                      </div>
                      <div>
                        <div className={cn('text-xs font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>Referencing tables</div>
                        <ul className="space-y-1 text-sm">
                          {referencingTables.map((ref, i) => (
                            <li key={i}>
                              <span className={colors.text}>{(ref.tableName as string)}</span>
                              <span className={cn('ml-1', colors.textMuted)}>{` (${ref.columnName as string})`}</span>
                            </li>
                          ))}
                          {referencingTables.length === 0 && <li className={colors.textMuted}>None</li>}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'sql' && (
                <div className="flex-1 flex flex-col p-4 overflow-hidden gap-4">
                  <div className={cn('flex-1 min-h-0 rounded-xl border overflow-hidden', colors.border)}>
                    <textarea
                      ref={sqlTextareaRef}
                      value={sqlQuery}
                      onChange={(e) => setSqlQuery(e.target.value)}
                      placeholder="SELECT * FROM ..."
                      className={cn(
                        'w-full h-full min-h-[120px] font-mono text-sm p-4 resize-none focus:outline-none',
                        colors.surface,
                        colors.text
                      )}
                      spellCheck={false}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={runSql}
                      className={cn('flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm', colors.primary, colors.primaryText)}
                    >
                      <PlayIcon />
                      Run query (SELECT only, max 1000 rows)
                    </button>
                    {recentQueries.length > 0 && (
                      <div className="relative">
                        <select
                          className={cn('border rounded-lg px-3 py-2 text-sm max-w-[200px]', colors.border, colors.surface, colors.text)}
                          value=""
                          onChange={(e) => {
                            const q = e.target.value;
                            if (q) setSqlQuery(q);
                            e.target.value = '';
                          }}
                        >
                          <option value="">Recent queries...</option>
                          {recentQueries.map((q, i) => (
                            <option key={i} value={q}>
                              {q.length > 50 ? q.slice(0, 47) + '...' : q}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <span className={cn('text-xs', colors.textMuted)}>Ctrl+Enter to run</span>
                  </div>
                  {sqlError && (
                    <div className={cn('p-3 rounded-lg text-sm', colors.error, 'text-white')}>{sqlError}</div>
                  )}
                  {sqlResult && (
                    <div className={cn('rounded-xl border overflow-hidden flex-1 min-h-0 flex flex-col', colors.border, colors.surface)}>
                      <div className={cn('px-4 py-2 border-b text-sm font-medium', colors.border, colors.text)}>
                        Result: {sqlResult.length} row{sqlResult.length !== 1 ? 's' : ''}
                      </div>
                      <div className="flex-1 overflow-auto">
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-sm min-w-max">
                            <thead>
                              <tr className={cn('border-b', colors.border)}>
                                {sqlResult.length > 0 &&
                                  Object.keys(sqlResult[0]).map((k) => (
                                    <th key={k} className={cn('text-left py-2 px-4 font-medium whitespace-nowrap', colors.text)}>
                                      {k}
                                    </th>
                                  ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sqlResult.map((row, i) => (
                                <tr key={i} className={cn('border-b', colors.border)}>
                                  {Object.values(row).map((v, j) => (
                                    <td key={j} className="py-2 px-4">
                                      {formatCellValue(v)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {sidebarCollapsed &&
        tablesFlyoutOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <FlyoutRailBackdrop onClose={() => setTablesFlyoutOpen(false)} />
            <FlyoutRailPanel
              left={tablesFlyoutLeft}
              ariaLabel="Database tables"
              className={cn('w-[min(18rem,calc(100vw-3.25rem))]', colors.surface, colors.border)}
            >
              <div className={cn('flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b min-h-[2.25rem]', colors.border)}>
                <span className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>Tables</span>
                <button
                  type="button"
                  onClick={() => setTablesFlyoutOpen(false)}
                  className={cn(
                    'flex-shrink-0 rounded-md p-1.5 text-sm leading-none transition-colors',
                    colors.textMuted,
                    colors.interactiveHover
                  )}
                  aria-label="Close tables list"
                >
                  ×
                </button>
              </div>
              <div className="p-2 border-b shrink-0">
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <SearchIcon />
                  </span>
                  <input
                    type="text"
                    value={tableFilter}
                    onChange={(e) => setTableFilter(e.target.value)}
                    placeholder="Filter tables..."
                    className={cn('w-full pl-8 pr-3 py-2 text-sm rounded-lg border', colors.border, colors.background, colors.text)}
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-2">
                {tablesLoading ? (
                  <p className={cn('text-sm py-4 text-center', colors.textMuted)}>Loading tables...</p>
                ) : filteredTables.length === 0 ? (
                  <p className={cn('text-sm py-4 text-center', colors.textMuted)}>
                    {tableFilter ? 'No tables match' : 'No tables'}
                  </p>
                ) : (
                  <div className="space-y-0.5">
                    {filteredTables.map((t) => (
                      <button
                        key={t.tableName}
                        type="button"
                        onClick={() => {
                          setSelectedTable(t.tableName);
                          setEditingRow(null);
                          setAddingRow(false);
                          setTablesFlyoutOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg text-sm transition-colors',
                          selectedTable === t.tableName
                            ? cn(colors.primary, colors.primaryText)
                            : cn(colors.text, colors.interactiveHover)
                        )}
                      >
                        <TableIcon />
                        <span className="truncate">{t.tableName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FlyoutRailPanel>
          </>,
          document.body
        )}

      <Modal
        isOpen={emptyTableModalOpen}
        onClose={() => {
          if (!emptyLoading) {
            setEmptyTableModalOpen(false);
            setEmptyConfirmName('');
            setEmptyCascade(false);
          }
        }}
        size="md"
        zIndex={110}
      >
        <ModalHeader>
          <ModalTitle>Empty table</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm mb-3', colors.text)}>
            This will delete <span className="font-semibold">all rows</span> in{' '}
            <code className={cn('text-xs px-1.5 py-0.5 rounded', colors.surface, colors.border)}>{selectedTable}</code>.
            Identity sequences are reset. This cannot be undone.
          </p>
          <label className={cn('block text-sm font-medium mb-1', colors.text)} htmlFor="empty-table-confirm">
            Type the table name to confirm
          </label>
          <input
            id="empty-table-confirm"
            type="text"
            value={emptyConfirmName}
            onChange={(e) => setEmptyConfirmName(e.target.value)}
            className={cn('w-full border rounded-lg px-3 py-2 text-sm mb-4', colors.border, colors.surface, colors.text)}
            placeholder={selectedTable ?? ''}
            autoComplete="off"
            disabled={emptyLoading}
          />
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={emptyCascade}
              onChange={(e) => setEmptyCascade(e.target.checked)}
              className="rounded mt-0.5"
              disabled={emptyLoading}
            />
            <span className={cn('text-sm', colors.text)}>
              <span className="font-medium text-red-700 dark:text-red-400">Cascade truncate</span>
              <span className={cn('block text-xs mt-0.5', colors.textMuted)}>
                Also truncates tables that reference this one. Only use if truncate fails because of foreign keys.
              </span>
            </span>
          </label>
        </ModalContent>
        <ModalFooter>
          <button
            type="button"
            onClick={() => {
              if (!emptyLoading) {
                setEmptyTableModalOpen(false);
                setEmptyConfirmName('');
                setEmptyCascade(false);
              }
            }}
            disabled={emptyLoading}
            className={cn('px-3 py-1.5 rounded-lg border text-sm', colors.border, colors.text)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleEmptyTable()}
            disabled={emptyConfirmName !== selectedTable || emptyLoading || !selectedTable}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {emptyLoading ? 'Working…' : 'Empty table'}
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={rowToDelete != null}
        onClose={() => setRowToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete row"
        message="This row will be permanently deleted. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deleteLoading}
      />
      <ConfirmationDialog
        isOpen={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={() => void handleBulkDeleteConfirm()}
        title="Delete selected rows"
        message={`Delete ${selectedRowIds.size} selected row(s)? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={bulkDeleteLoading}
      />
    </div>
  );
}
